import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '../context/UserContext';
import { getNoOperados, getNoOperadosDeudores, getNoOperadosCesiones } from '../api/apiConfig';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { FaSort, FaSortDown, FaSortUp, FaStar, FaPhoneAlt, FaEnvelope, FaUniversity, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa';
import { formatMoneyCLP } from '../utils/numberFormat';

const PRIORIDAD_COLORS = { 1: '#16a34a', 2: '#d97706', 3: '#dc2626' };
const PRIORIDAD_LABELS = { 1: 'Alta', 2: 'Media', 3: 'Baja' };
const GRUPO_COLORS = { buena: '#16a34a', media: '#d97706', baja: '#dc2626', sin: '#9ca3af' };

const Badge = ({ children, color = '#6b7280', bg }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ color, backgroundColor: bg || `${color}18` }}>
    {children}
  </span>
);

const ScoreBar = ({ value, max = 11 }) => {
  const pct = Math.min(100, (value / max) * 100);
  const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold" style={{ color }}>{value}</span>
    </div>
  );
};

const DonutMini = ({ buena = 0, media = 0, baja = 0, sin = 0 }) => {
  const b = Number(buena) || 0;
  const m = Number(media) || 0;
  const bj = Number(baja) || 0;
  const s = Number(sin) || 0;
  const total = b + m + bj + s;
  if (total === 0) return <span className="text-xs text-gray-400">Sin datos</span>;
  const allSegments = [
    { pct: b, color: GRUPO_COLORS.buena, label: 'Buena' },
    { pct: m, color: GRUPO_COLORS.media, label: 'Media' },
    { pct: bj, color: GRUPO_COLORS.baja, label: 'Baja' },
    { pct: s, color: GRUPO_COLORS.sin, label: 'Sin' },
  ];
  const segments = allSegments.filter(seg => seg.pct >= 0.1);
  let offset = 0;
  return (
    <div className="flex items-center gap-3">
      <svg width="40" height="40" viewBox="0 0 36 36">
        {segments.map((seg, i) => {
          const dash = seg.pct;
          const gap = 100 - dash;
          const el = (
            <circle key={i} cx="18" cy="18" r="15.9" fill="none" strokeWidth="3.5"
              stroke={seg.color} strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-offset}
              transform="rotate(-90 18 18)" />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className="flex flex-col text-[11px] leading-relaxed">
        {segments.map((seg, i) => (
          <span key={i} className="font-semibold" style={{ color: seg.color }}>{seg.label}: {seg.pct.toFixed(1)}%</span>
        ))}
      </div>
    </div>
  );
};

const PanelNoOperados = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCol, setSortCol] = useState('score_total');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRut, setExpandedRut] = useState(null);
  const [deudores, setDeudores] = useState([]);
  const [cesiones, setCesiones] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTab, setDrillTab] = useState('resumen');
  const [filterPrioridad, setFilterPrioridad] = useState('todas');
  const [filterTamano, setFilterTamano] = useState('todos');
  const [filterSegmento, setFilterSegmento] = useState('todos');

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const result = await getNoOperados();
        if (!mounted) return;
        setRows(result?.data ?? []);
      } catch { setRows([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [user]);

  const filtered = useMemo(() => {
    let data = rows;
    if (filterPrioridad !== 'todas') data = data.filter(r => String(r.prioridad) === filterPrioridad);
    if (filterTamano !== 'todos') data = data.filter(r => r.tamano === filterTamano);
    if (filterSegmento !== 'todos') data = data.filter(r => (r.segmento || 'SIN ASIGNACION') === filterSegmento);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      data = data.filter(r => [r.rut, r.cliente, r.ejecutivo].some(v => v && String(v).toLowerCase().includes(q)));
    }
    return data;
  }, [rows, searchTerm, filterPrioridad, filterTamano, filterSegmento]);

  const sorted = useMemo(() => {
    if (!sortCol) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) av = '';
      if (bv == null) bv = '';
      const an = Number(av), bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn)) return dir * (an - bn);
      return dir * String(av).localeCompare(String(bv), 'es', { numeric: true });
    });
  }, [filtered, sortCol, sortDir]);

  const tamanos = useMemo(() => [...new Set(rows.map(r => r.tamano).filter(Boolean))].sort(), [rows]);
  const segmentos = useMemo(() => [...new Set(rows.map(r => r.segmento || 'SIN ASIGNACION'))].sort(), [rows]);
  const stats = useMemo(() => ({
    total: rows.length,
    p1: rows.filter(r => r.prioridad === 1).length,
    p2: rows.filter(r => r.prioridad === 2).length,
    p3: rows.filter(r => r.prioridad === 3).length,
  }), [rows]);

  const exportExcel = () => {
    const headers = ['Prioridad','RUT','Cliente','Segmento','Ejecutivo','Tamaño','Ventas Prom 12M (MM$)','Score Total','S.Tamaño','S.Contacto','S.Deudores','S.Banco','Teléfono','Tel Bueno','Correo','Correo Bueno','% Buena','% Media','% Baja','% Sin','% Banco 6m','Cesiones Mes ($)','Días s/operar','Línea','Venc. Línea','Últ. Operación'];
    const rows_csv = sorted.map(r => [
      PRIORIDAD_LABELS[r.prioridad] || r.prioridad,
      r.rut, r.cliente, r.segmento || 'SIN ASIGNACION', r.ejecutivo, r.tamano,
      r.ventas_promedio_mm ?? '',
      r.score_total, r.score_tamano, r.score_contacto, r.score_deudores, r.score_banco,
      r.telefono ?? '', r.telefono_bueno ? 'Sí' : 'No',
      r.correo ?? '', r.correo_bueno ? 'Sí' : 'No',
      r.composicion_deudores?.pct_buena ?? 0,
      r.composicion_deudores?.pct_media ?? 0,
      r.composicion_deudores?.pct_baja ?? 0,
      r.composicion_deudores?.pct_sin ?? 0,
      r.pct_banco ?? 0,
      r.monto_cesiones_mes ?? 0,
      r.dias_desde_ult_ope ?? '',
      r.linea_estado ?? '', r.fec_venc ?? '', r.ult_ope ?? '',
    ]);
    const escape = v => { const s = v == null ? '' : String(v); return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
    const content = [headers.map(escape).join(';'), ...rows_csv.map(row => row.map(escape).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clientes_no_operados_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return <FaSort className="ml-1 opacity-30" />;
    return sortDir === 'asc' ? <FaSortUp className="ml-1" /> : <FaSortDown className="ml-1" />;
  };

  const toggleExpand = async (rut) => {
    if (expandedRut === rut) { setExpandedRut(null); return; }
    setExpandedRut(rut);
    setDrillTab('resumen');
    setDeudores([]);
    setCesiones([]);
    setDrillLoading(false);
  };

  const loadDrill = async (tab, rut) => {
    setDrillTab(tab);
    if (tab === 'deudores') {
      setDrillLoading(true);
      try {
        const res = await getNoOperadosDeudores(rut);
        setDeudores(res?.deudores ?? []);
      } catch (err) { console.error('Error deudores:', err); setDeudores([]); }
      finally { setDrillLoading(false); }
    }
    if (tab === 'cesiones') {
      setDrillLoading(true);
      try {
        const res = await getNoOperadosCesiones(rut);
        setCesiones(res?.cesiones ?? []);
      } catch (err) { console.error('Error cesiones:', err); setCesiones([]); }
      finally { setDrillLoading(false); }
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Clientes No Operados</h1>
        <p className="text-gray-500 text-sm mt-1">Clientes que no han operado en 6+ meses, con cesiones activas en el mercado. Priorizados por scoring.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total candidatos', value: stats.total, color: '#1e40af' },
          { label: 'Prioridad Alta', value: stats.p1, color: PRIORIDAD_COLORS[1] },
          { label: 'Prioridad Media', value: stats.p2, color: PRIORIDAD_COLORS[2] },
          { label: 'Prioridad Baja', value: stats.p3, color: PRIORIDAD_COLORS[3] },
        ].map((kpi, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{kpi.label}</div>
            <div className="text-2xl font-bold mt-1" style={{ color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1" style={{ minWidth: 250 }}>
          <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar RUT, cliente o ejecutivo..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <IoClose />
            </button>
          )}
        </div>
        <select value={filterPrioridad} onChange={e => setFilterPrioridad(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="todas">Todas las prioridades</option>
          <option value="1">🟢 Alta</option>
          <option value="2">🟡 Media</option>
          <option value="3">🔴 Baja</option>
        </select>
        <select value={filterTamano} onChange={e => setFilterTamano(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="todos">Todos los tamaños</option>
          {tamanos.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterSegmento} onChange={e => setFilterSegmento(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="todos">Todos los segmentos</option>
          {segmentos.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-sm text-gray-500">{sorted.length} de {rows.length} clientes</span>
        <button onClick={exportExcel}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: '#16a34a' }}
          title="Descargar tabla filtrada como Excel/CSV">
          <FaFileExcel className="w-4 h-4" /> Descargar Excel
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8 text-red-600" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-10 text-gray-400">Sin resultados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm border-collapse" style={{ minWidth: 1150 }}>
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  {[
                    { label: '', w: 40 },
                    { label: 'Prior.', w: 75 },
                    { label: 'RUT', w: 105 },
                    { label: 'Cliente', w: 210 },
                    { label: 'Segmento', w: 110 },
                    { label: 'Tamaño', w: 85 },
                    { label: 'Score', w: 115 },
                    { label: 'S.Tam', w: 55, align: 'center' },
                    { label: 'S.Cont', w: 75, align: 'center' },
                    { label: 'S.Deud', w: 55, align: 'center' },
                    { label: 'S.Banco', w: 75, align: 'center' },
                    { label: 'Cesiones mes ($)', w: 130, align: 'right' },
                    { label: 'Días s/operar', w: 95, align: 'center' },
                  ].map((col, i) => {
                    const keys = ['_expand','prioridad','rut','cliente','segmento','tamano','score_total','score_tamano','score_contacto','score_deudores','score_banco','monto_cesiones_mes','dias_desde_ult_ope'];
                    const key = keys[i];
                    return (
                      <th key={i}
                        style={{ width: col.w, textAlign: col.align || 'left' }}
                        className="px-3 py-2 border border-gray-200 cursor-pointer select-none whitespace-nowrap"
                        onClick={() => key !== '_expand' && handleSort(key)}>
                        <span className="inline-flex items-center text-xs font-semibold text-gray-700">
                          {col.label}{key !== '_expand' && sortIcon(key)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const isExpanded = expandedRut === r.rut;
                  return (
                    <React.Fragment key={r.rut}>
                      <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleExpand(r.rut)}>
                        <td style={{width:40}} className="px-2 text-center text-gray-400 border border-gray-200">
                          {isExpanded ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
                        </td>
                        <td style={{width:75}} className="px-3 py-2 border border-gray-200">
                          <Badge color="#fff" bg={PRIORIDAD_COLORS[r.prioridad]}>
                            {PRIORIDAD_LABELS[r.prioridad] || r.prioridad}
                          </Badge>
                        </td>
                        <td style={{width:105}} className="px-3 py-2 font-mono text-xs border border-gray-200">{r.rut}</td>
                        <td style={{width:210}} className="px-3 py-2 font-medium truncate border border-gray-200" title={r.cliente}>{r.cliente}</td>
                        <td style={{width:110}} className="px-3 py-2 border border-gray-200 text-xs">{r.segmento || 'SIN ASIGNACION'}</td>
                        <td style={{width:85}} className="px-3 py-2 border border-gray-200"><Badge color={r.tamano === 'Grande' ? '#1e40af' : '#6b7280'}>{r.tamano}</Badge></td>
                        <td style={{width:115}} className="px-3 py-2 border border-gray-200"><ScoreBar value={r.score_total} /></td>
                        <td style={{width:55}} className="px-3 py-2 text-center border border-gray-200">{r.score_tamano}</td>
                        <td style={{width:75}} className="px-3 py-2 text-center border border-gray-200">
                          <span className="inline-flex items-center gap-1">
                            {r.score_contacto}
                            {r.telefono_bueno && <FaPhoneAlt className="w-2.5 h-2.5 text-green-600" />}
                            {r.correo_bueno && <FaEnvelope className="w-2.5 h-2.5 text-green-600" />}
                          </span>
                        </td>
                        <td style={{width:55}} className="px-3 py-2 text-center border border-gray-200">{r.score_deudores}</td>
                        <td style={{width:75}} className="px-3 py-2 text-center border border-gray-200">
                          <span className="inline-flex items-center gap-1">
                            {r.score_banco}
                            {r.pct_banco > 20 && <FaUniversity className="w-2.5 h-2.5 text-red-500" />}
                          </span>
                        </td>
                        <td style={{width:130}} className="px-3 py-2 text-right font-mono text-xs border border-gray-200">{formatMoneyCLP(r.monto_cesiones_mes)}</td>
                        <td style={{width:95}} className="px-3 py-2 text-center border border-gray-200">{r.dias_desde_ult_ope ?? '—'}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={13} className="p-0">
                            <div className="bg-slate-50 border-t border-b border-blue-200 p-4">
                              {/* Tabs */}
                              <div className="flex gap-2 mb-4">
                                {['resumen', 'deudores', 'cesiones'].map(tab => (
                                  <button key={tab}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${drillTab === tab ? 'bg-red-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                                    onClick={(e) => { e.stopPropagation(); loadDrill(tab, r.rut); }}>
                                    {tab === 'resumen' ? 'Resumen' : tab === 'deudores' ? 'Deudores' : 'Cesiones'}
                                  </button>
                                ))}
                              </div>

                              {drillTab === 'resumen' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="text-xs text-gray-500 uppercase mb-2">Información general</div>
                                    <div className="space-y-1 text-sm">
                                      <div><span className="text-gray-500">Ejecutivo:</span> <span className="font-medium">{r.ejecutivo}</span></div>
                                      <div><span className="text-gray-500">Línea:</span> {r.linea_estado}</div>
                                      <div><span className="text-gray-500">Venc. línea:</span> {r.fec_venc}</div>
                                      <div><span className="text-gray-500">Últ. operación:</span> {r.ult_ope || 'Nunca'}</div>
                                      <div><span className="text-gray-500">Ventas prom. 12M:</span> {r.ventas_promedio_mm ? `${Number(r.ventas_promedio_mm).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MM$` : '—'}</div>
                                    </div>
                                  </div>
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="text-xs text-gray-500 uppercase mb-2">Contactabilidad</div>
                                    <div className="space-y-1 text-sm">
                                      <div className="flex items-center gap-2">
                                        <FaPhoneAlt className={`w-3 h-3 ${r.telefono_bueno ? 'text-green-600' : 'text-gray-300'}`} />
                                        <span>{r.telefono || 'Sin teléfono'}</span>
                                        {r.telefono_bueno && <Badge color="#16a34a">Bueno</Badge>}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <FaEnvelope className={`w-3 h-3 ${r.correo_bueno ? 'text-green-600' : 'text-gray-300'}`} />
                                        <span className="truncate max-w-[200px]" title={r.correo}>{r.correo || 'Sin correo'}</span>
                                        {r.correo_bueno && <Badge color="#16a34a">Bueno</Badge>}
                                      </div>
                                      <div className="mt-2">
                                        <span className="text-gray-500">% Banco (6m):</span>{' '}
                                        <span className={`font-semibold ${r.pct_banco > 20 ? 'text-red-600' : 'text-green-600'}`}>
                                          {r.pct_banco?.toFixed(1)}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <div className="text-xs text-gray-500 uppercase mb-2">Composición deudores</div>
                                    <DonutMini
                                      buena={r.composicion_deudores?.pct_buena || 0}
                                      media={r.composicion_deudores?.pct_media || 0}
                                      baja={r.composicion_deudores?.pct_baja || 0}
                                      sin={r.composicion_deudores?.pct_sin || 0}
                                    />
                                    <div className="mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-400 leading-relaxed">
                                      <div><span className="font-semibold" style={{color: GRUPO_COLORS.buena}}>Buena:</span> A1–A4, E1–E4, T11–T13</div>
                                      <div><span className="font-semibold" style={{color: GRUPO_COLORS.media}}>Media:</span> T4–T10</div>
                                      <div><span className="font-semibold" style={{color: GRUPO_COLORS.baja}}>Baja:</span> T1–T3</div>
                                      <div><span className="font-semibold" style={{color: GRUPO_COLORS.sin}}>Sin:</span> Sin clasificación</div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {drillTab === 'deudores' && (
                                drillLoading ? (
                                  <div className="flex justify-center py-6"><AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-red-600" /></div>
                                ) : deudores.length === 0 ? (
                                  <div className="text-center py-6 text-gray-400 text-sm">Sin datos de deudores</div>
                                ) : (
                                  <div className="overflow-x-auto max-h-80">
                                    <table className="w-full text-xs">
                                      <thead><tr className="bg-gray-100">
                                        <th className="px-2 py-1 text-left">RUT Deudor</th>
                                        <th className="px-2 py-1 text-left">Nombre</th>
                                        <th className="px-2 py-1 text-left">Clasificación</th>
                                        <th className="px-2 py-1 text-right">Monto</th>
                                        <th className="px-2 py-1 text-left">Fecha</th>
                                      </tr></thead>
                                      <tbody>
                                        {deudores.map((d, i) => (
                                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                            <td className="px-2 py-1 font-mono">{d.RUT_DEUDOR}</td>
                                            <td className="px-2 py-1 max-w-[200px] truncate">{d.NOMBRE_DEUDOR}</td>
                                            <td className="px-2 py-1"><Badge>{d.CLASIFICACION || 'Sin'}</Badge></td>
                                            <td className="px-2 py-1 text-right font-mono">{formatMoneyCLP(d.MONTO)}</td>
                                            <td className="px-2 py-1">{d.FECHA_DOCUMENTO}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )
                              )}

                              {drillTab === 'cesiones' && (
                                drillLoading ? (
                                  <div className="flex justify-center py-6"><AiOutlineLoading3Quarters className="animate-spin h-6 w-6 text-red-600" /></div>
                                ) : cesiones.length === 0 ? (
                                  <div className="text-center py-6 text-gray-400 text-sm">Sin datos de cesiones</div>
                                ) : (
                                  <div className="overflow-x-auto max-h-80">
                                    <table className="w-full text-xs">
                                      <thead><tr className="bg-gray-100">
                                        <th className="px-2 py-1 text-left">Fecha</th>
                                        <th className="px-2 py-1 text-left">Deudor</th>
                                        <th className="px-2 py-1 text-left">Cesionario</th>
                                        <th className="px-2 py-1 text-left">Tipo</th>
                                        <th className="px-2 py-1 text-right">Monto</th>
                                      </tr></thead>
                                      <tbody>
                                        {cesiones.map((c, i) => (
                                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                            <td className="px-2 py-1">{c.FCH_CESION}</td>
                                            <td className="px-2 py-1 max-w-[200px] truncate">{c.DEUDOR}</td>
                                            <td className="px-2 py-1 max-w-[200px] truncate">{c.CESIONARIO}</td>
                                            <td className="px-2 py-1">
                                              <Badge color={c.TIPO_CESIONARIO === 'BANCO' ? '#dc2626' : c.TIPO_CESIONARIO === 'IF' ? '#2563eb' : '#6b7280'}>
                                                {c.TIPO_CESIONARIO}
                                              </Badge>
                                            </td>
                                            <td className="px-2 py-1 text-right font-mono">{formatMoneyCLP(c.MONTO)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelNoOperados;
